import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input, InputPassword } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAddAwsSmtp } from "@/hooks/use-mailbox";
import { extractErrorMessage } from "@/lib/utils";
import { AwsSmtpPayload } from "@/types/mailbox.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

const formSchema = z.object({
  email: z.string().min(1, "Email is required"),
  awsRegion: z.string().min(1, "AWS region is required"),
  awsAccessKey: z.string().min(1, "AWS access key is required"),
  awsSecret: z.string().min(1, "AWS secret is required"),
});

type FormData = z.infer<typeof formSchema>;

interface AwsSesWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AWS_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)" },
  { value: "us-east-2", label: "US East (Ohio)" },
  { value: "us-west-1", label: "US West (N. California)" },
  { value: "us-west-2", label: "US West (Oregon)" },
  { value: "ap-south-1", label: "Asia Pacific (Mumbai)" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  { value: "ap-northeast-2", label: "Asia Pacific (Seoul)" },
  { value: "ca-central-1", label: "Canada (Central)" },
  { value: "eu-central-1", label: "Europe (Frankfurt)" },
  { value: "eu-west-1", label: "Europe (Ireland)" },
  { value: "eu-west-2", label: "Europe (London)" },
  { value: "eu-west-3", label: "Europe (Paris)" },
  { value: "eu-north-1", label: "Europe (Stockholm)" },
  { value: "me-south-1", label: "Middle East (Bahrain)" },
  { value: "sa-east-1", label: "South America (São Paulo)" },
];

export function AwsSesWizard({ open, onOpenChange }: AwsSesWizardProps) {
  const { mutateAsync: addAwsSmtp, isPending } = useAddAwsSmtp();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      awsRegion: "",
      awsAccessKey: "",
      awsSecret: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      const payload: AwsSmtpPayload = {
        ...data,
        firstName: "",
        lastName: "",
        smtpHost: `email-smtp.${data.awsRegion}.amazonaws.com`,
        smtpUsername: data.awsAccessKey,
        smtpPassword: data.awsSecret,
        smtpPort: 587,
        smtpTls: "TLS",
      };

      await addAwsSmtp(payload);
      toast.success("AWS SES settings have been successfully configured.");
      onOpenChange(false);
      form.reset();
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Failed to configure AWS SES settings."
      );
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Amazon SES Configuration</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            autoComplete="off"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter email"
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="awsRegion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AWS region *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select AWS region" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {AWS_REGIONS.map((region) => (
                        <SelectItem key={region.value} value={region.value}>
                          {region.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="awsAccessKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AWS access key *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your AWS access key"
                      type="text"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="awsSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AWS secret *</FormLabel>
                  <FormControl>
                    <InputPassword
                      {...field}
                      placeholder="Enter your AWS secret"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save Configuration"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
